import { OpenWakeWord, type WakeWordOptions } from "./open-wake-word";

export const DEFAULT_MODELS_BASE_URL = "/models/";

export type ResolvedWakeWordModel = {
  /** Value passed to OpenWakeWord (absolute/relative onnx URL). */
  model: string;
  requestedPath: string;
};

function toModelUrl(path: string, baseUrl: string): string {
  const trimmed = path.trim();
  if (/^https?:\/\//i.test(trimmed) || trimmed.startsWith("/")) {
    return trimmed;
  }
  const base = baseUrl.endsWith("/") ? baseUrl : `${baseUrl}/`;
  return `${base}${trimmed.replace(/^\.\//, "")}`;
}

/** Resolve the tenant WakeWordOnnxPath; no invented fallback model. */
export async function resolveWakeWordModel(
  customPath: string | null | undefined,
  baseUrl: string = DEFAULT_MODELS_BASE_URL,
): Promise<ResolvedWakeWordModel | null> {
  if (!customPath?.trim()) {
    return null;
  }

  const url = toModelUrl(customPath, baseUrl);
  try {
    const res = await fetch(url, { method: "HEAD", cache: "no-store" });
    if (res.ok) {
      return { model: url, requestedPath: customPath.trim() };
    }
  } catch {
    return null;
  }

  return null;
}

export async function createWakeWordDetector(opts: {
  wakePhrase?: string | null;
  wakeWordOnnxPath?: string | null;
  baseUrl?: string;
  wasmPaths?: string;
  threshold?: number;
}): Promise<{
  detector: OpenWakeWord;
  resolved: ResolvedWakeWordModel;
  wakePhrase: string;
}> {
  const wakePhrase = opts.wakePhrase?.trim();
  if (!wakePhrase) {
    throw new Error("Wake phrase is not configured for this tenant.");
  }

  const baseUrl = opts.baseUrl ?? DEFAULT_MODELS_BASE_URL;
  const resolved = await resolveWakeWordModel(opts.wakeWordOnnxPath, baseUrl);
  if (!resolved) {
    throw new Error(
      "Wake word model is not configured for this tenant (WakeWordOnnxPath).",
    );
  }

  const createOpts: WakeWordOptions = {
    baseUrl,
    wasmPaths: opts.wasmPaths ?? "/ort/",
    wakewordModels: [resolved.model],
    threshold: opts.threshold ?? 0.95,
  };

  const detector = await OpenWakeWord.create(createOpts);
  return {
    detector,
    resolved,
    wakePhrase,
  };
}
